/**
 * Ingredients Index Page
 *
 * Lists all ingredients with filtering, sorting, and two-column layout
 * showing ingredients table and detail sidebar.
 */

import { useState } from 'react';
import { Head, Link, usePage, router } from '@inertiajs/react';
import { Navbar } from '@/components';
import { IngredientDetailSidebar } from '@/components/food/IngredientDetailSidebar';
import { AddIngredientForm } from '@/components/food/AddIngredientForm';
import type { IngredientsIndexProps, IngredientWithCount, IngredientWithDetails } from '@/types/food';
import { getCategoryStyle } from '@/types/food';
import type { SharedProps } from '@/types';

type PageProps = IngredientsIndexProps & SharedProps;

type SortOption = 'name_asc' | 'name_desc' | 'most_used' | 'recently_added';

export default function IngredientsIndex() {
  const { ingredients, categories } = usePage<PageProps>().props;

  const [searchValue, setSearchValue] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [sortOption, setSortOption] = useState<SortOption>('name_asc');
  const [selectedIngredientId, setSelectedIngredientId] = useState<number | null>(null);

  // Filter and sort ingredients
  const filteredIngredients = filterAndSort(ingredients, searchValue, categoryFilter, sortOption);

  // Find selected ingredient
  const selectedIngredient: IngredientWithDetails | null =
    selectedIngredientId
      ? (ingredients.find((i) => i.id === selectedIngredientId) as IngredientWithDetails) || null
      : null;

  // Calculate stats
  const stats = {
    total: ingredients.length,
    categories: new Set(ingredients.map((i) => i.category).filter(Boolean)).size,
    withSubstitutes: ingredients.filter((i) => i.substitutes.length > 0).length,
    unused: ingredients.filter((i) => i.recipeCount === 0).length,
  };

  const [editingId, setEditingId] = useState<number | null>(null);

  const handleDeleteIngredient = async (id: number, name: string) => {
    if (!confirm(`Are you sure you want to delete "${name}"?`)) {
      return;
    }

    try {
      const response = await fetch(`/food/ingredients/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
      });

      if (response.ok) {
        if (selectedIngredientId === id) {
          setSelectedIngredientId(null);
        }
        router.reload({ only: ['ingredients'] });
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to delete ingredient');
      }
    } catch {
      alert('Failed to delete ingredient');
    }
  };

  const handleEditIngredient = async (
    id: number,
    updates: { name: string; category: string; default_unit: string }
  ) => {
    try {
      const response = await fetch(`/food/ingredients/${id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-CSRF-Token': getCSRFToken(),
        },
        body: JSON.stringify({ ingredient: updates }),
      });

      if (response.ok) {
        setEditingId(null);
        router.reload({ only: ['ingredients'] });
      } else {
        const data = await response.json();
        alert(data.error || 'Failed to update ingredient');
      }
    } catch {
      alert('Failed to update ingredient');
    }
  };

  return (
    <>
      <Head title="Ingredients" />
      <div className="min-h-screen bg-gray-50">
        <Navbar currentSection="food" />

        <main className="max-w-7xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Ingredients</h1>
            <Link
              href="/food/recipes"
              className="text-sm text-blue-600 hover:text-blue-700 font-medium"
            >
              Back to Recipes
            </Link>
          </div>

          {/* Toolbar */}
          <div className="flex flex-wrap gap-3 mb-5">
            <input
              type="text"
              placeholder="Search ingredients..."
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="flex-1 min-w-[250px] px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20"
            />

            <select
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
            >
              <option value="">All Categories</option>
              {categories.map((cat) => (
                <option key={cat} value={cat}>
                  {cat.charAt(0).toUpperCase() + cat.slice(1)}
                </option>
              ))}
            </select>

            <select
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value as SortOption)}
              className="px-4 py-2.5 border border-gray-200 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:border-blue-500"
            >
              <option value="name_asc">Sort: Name A-Z</option>
              <option value="name_desc">Sort: Name Z-A</option>
              <option value="most_used">Sort: Most Used</option>
              <option value="recently_added">Sort: Recently Added</option>
            </select>
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-6">
            {/* Ingredients Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Table Header */}
              <div className="grid grid-cols-[1fr_150px_100px_100px] px-5 py-3 bg-gray-50 border-b border-gray-200">
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Ingredient
                </span>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Category
                </span>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Default Unit
                </span>
                <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                  Actions
                </span>
              </div>

              {/* Table Body */}
              {filteredIngredients.length > 0 ? (
                <div>
                  {filteredIngredients.map((ingredient) => (
                    <IngredientRow
                      key={ingredient.id}
                      ingredient={ingredient}
                      categories={categories}
                      isSelected={selectedIngredientId === ingredient.id}
                      isEditing={editingId === ingredient.id}
                      onSelect={() => setSelectedIngredientId(ingredient.id)}
                      onEdit={() => setEditingId(ingredient.id)}
                      onCancelEdit={() => setEditingId(null)}
                      onSaveEdit={(updates) => handleEditIngredient(ingredient.id, updates)}
                      onDelete={() => handleDeleteIngredient(ingredient.id, ingredient.name)}
                    />
                  ))}
                </div>
              ) : (
                <div className="py-12 text-center text-gray-500">
                  <svg
                    className="w-12 h-12 mx-auto text-gray-300 mb-3"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={1}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z"
                    />
                  </svg>
                  <p className="text-sm">No ingredients found</p>
                </div>
              )}

              {/* Add Ingredient Form */}
              <AddIngredientForm categories={categories} />

              {/* Pagination placeholder */}
              <div className="flex justify-between items-center px-5 py-4 border-t border-gray-200">
                <span className="text-sm text-gray-500">
                  Showing {filteredIngredients.length} of {ingredients.length} ingredients
                </span>
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    disabled
                  >
                    Previous
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm"
                  >
                    1
                  </button>
                  <button
                    type="button"
                    className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white hover:bg-gray-50 disabled:opacity-50"
                    disabled
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <IngredientDetailSidebar
              ingredient={selectedIngredient}
              allIngredients={ingredients}
              stats={stats}
            />
          </div>
        </main>
      </div>
    </>
  );
}

const UNIT_OPTIONS = ['g', 'ml', 'pcs', 'tbsp', 'tsp', 'cups', 'cloves', 'oz', 'lb', 'kg'];

interface IngredientRowProps {
  ingredient: IngredientWithCount;
  categories: string[];
  isSelected: boolean;
  isEditing: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onCancelEdit: () => void;
  onSaveEdit: (updates: { name: string; category: string; default_unit: string }) => void;
  onDelete: () => void;
}

function IngredientRow({
  ingredient,
  categories,
  isSelected,
  isEditing,
  onSelect,
  onEdit,
  onCancelEdit,
  onSaveEdit,
  onDelete,
}: IngredientRowProps) {
  const categoryStyle = getCategoryStyle(ingredient.category);
  const [editName, setEditName] = useState(ingredient.name);
  const [editCategory, setEditCategory] = useState(ingredient.category || '');
  const [editUnit, setEditUnit] = useState(ingredient.defaultUnit || 'g');

  const handleSave = () => {
    if (!editName.trim()) {
      alert('Name is required');
      return;
    }
    onSaveEdit({
      name: editName.trim(),
      category: editCategory,
      default_unit: editUnit,
    });
  };

  if (isEditing) {
    return (
      <div className="grid grid-cols-[1fr_150px_100px_100px] px-5 py-3 border-b border-gray-100 items-center bg-blue-50">
        <input
          type="text"
          value={editName}
          onChange={(e) => setEditName(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm focus:outline-none focus:border-blue-500"
          autoFocus
        />
        <select
          value={editCategory}
          onChange={(e) => setEditCategory(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:border-blue-500"
        >
          <option value="">Select...</option>
          {categories.map((cat) => (
            <option key={cat} value={cat}>
              {cat.charAt(0).toUpperCase() + cat.slice(1)}
            </option>
          ))}
        </select>
        <select
          value={editUnit}
          onChange={(e) => setEditUnit(e.target.value)}
          className="px-2 py-1.5 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:border-blue-500"
        >
          {UNIT_OPTIONS.map((u) => (
            <option key={u} value={u}>
              {u}
            </option>
          ))}
        </select>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={handleSave}
            className="px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 transition-colors"
          >
            Save
          </button>
          <button
            type="button"
            onClick={onCancelEdit}
            className="px-3 py-1.5 bg-gray-200 text-gray-700 text-xs font-medium rounded hover:bg-gray-300 transition-colors"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`grid grid-cols-[1fr_150px_100px_100px] px-5 py-4 border-b border-gray-100 items-center cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'
      }`}
      onClick={onSelect}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          onSelect();
        }
      }}
    >
      <span className="font-medium text-gray-900">{ingredient.name}</span>
      <span>
        {ingredient.category && (
          <span
            className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium capitalize ${categoryStyle.bg} ${categoryStyle.text}`}
          >
            {ingredient.category}
          </span>
        )}
      </span>
      <span className="text-sm text-gray-500">{ingredient.defaultUnit || '-'}</span>
      <div className="flex gap-2" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onEdit}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md text-gray-500 hover:bg-gray-200 hover:text-gray-700 transition-colors"
          title="Edit"
          aria-label={`Edit ${ingredient.name}`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"
            />
          </svg>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="w-8 h-8 flex items-center justify-center bg-gray-100 rounded-md text-gray-500 hover:bg-red-50 hover:text-red-600 transition-colors"
          title="Delete"
          aria-label={`Delete ${ingredient.name}`}
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}>
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
            />
          </svg>
        </button>
      </div>
    </div>
  );
}

function filterAndSort(
  ingredients: IngredientWithCount[],
  search: string,
  category: string,
  sort: SortOption
): IngredientWithCount[] {
  let result = [...ingredients];

  // Filter by search
  if (search) {
    const searchLower = search.toLowerCase();
    result = result.filter((i) => i.name.toLowerCase().includes(searchLower));
  }

  // Filter by category
  if (category) {
    result = result.filter((i) => i.category === category);
  }

  // Sort
  switch (sort) {
    case 'name_asc':
      result.sort((a, b) => a.name.localeCompare(b.name));
      break;
    case 'name_desc':
      result.sort((a, b) => b.name.localeCompare(a.name));
      break;
    case 'most_used':
      result.sort((a, b) => b.recipeCount - a.recipeCount);
      break;
    case 'recently_added':
      result.sort((a, b) => b.id - a.id);
      break;
  }

  return result;
}

function getCSRFToken(): string {
  const meta = document.querySelector('meta[name="csrf-token"]');
  return meta?.getAttribute('content') || '';
}
